import { Search, X, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
}

interface DataTableFiltersProps {
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchClear?: () => void;
  filters?: FilterConfig[];
  onClearAll?: () => void;
}

export function DataTableFilters({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  onSearchClear,
  filters = [],
  onClearAll,
}: DataTableFiltersProps) {
  const hasActiveFilters = filters.some((f) => f.value && f.value !== 'all') || searchValue;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        {onSearchChange && (
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchValue && onSearchClear && (
              <button
                onClick={onSearchClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {filters.map((filter) => (
          <Select key={filter.key} value={filter.value} onValueChange={filter.onChange}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder={filter.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {filter.label}</SelectItem>
              {filter.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        {hasActiveFilters && onClearAll && (
          <Button variant="ghost" size="sm" onClick={onClearAll}>
            Clear filters
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          {searchValue && (
            <Badge variant="secondary" className="gap-1">
              Search: {searchValue}
              {onSearchClear && (
                <button onClick={onSearchClear} className="ml-1 hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          )}
          {filters
            .filter((f) => f.value && f.value !== 'all')
            .map((filter) => (
              <Badge key={filter.key} variant="secondary" className="gap-1">
                {filter.label}: {filter.options.find((o) => o.value === filter.value)?.label}
                <button onClick={() => filter.onChange('all')} className="ml-1 hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
        </div>
      )}
    </div>
  );
}
